import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";

export default function ArtistProfileModal({ isClient, artist, onContact, onClose }) {
  if (!isClient || !artist) return null;

  const hasCover = Boolean(artist.cover_image_url);
  const coverStyle = hasCover
    ? {
        backgroundImage: `linear-gradient(160deg, rgba(9,9,11,0.6), rgba(9,9,11,0.88)), url(${artist.cover_image_url})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : undefined;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-8"
      onClick={onClose}
    >
      <div
        className="panel-strong max-h-[90vh] w-full max-w-lg overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cover / header */}
        <div
          className={`rounded-t-xl p-6 ${hasCover ? "text-white" : ""}`}
          style={coverStyle}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className={`text-[11px] font-semibold uppercase tracking-[0.12em] ${hasCover ? "text-white/60" : "text-zinc-400 dark:text-zinc-500"}`}>
                Artist
              </p>
              <h2 className={`mt-0.5 text-xl font-bold tracking-tight ${hasCover ? "text-white" : "text-zinc-900 dark:text-zinc-50"}`}>
                {artist.name}
              </h2>
              <p className={`text-xs ${hasCover ? "text-white/60" : "text-zinc-400 dark:text-zinc-500"}`}>
                @{artist.username}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`shrink-0 rounded-lg border p-1.5 transition ${hasCover ? "border-white/20 text-white/70 hover:text-white" : "border-zinc-200/60 text-zinc-400 hover:text-zinc-600 dark:border-white/[0.07] dark:hover:text-zinc-200"}`}
              aria-label="Close"
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M1 1l12 12M13 1L1 13"/>
              </svg>
            </button>
          </div>

          {artist.genres?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {artist.genres.map((g) => (
                <span
                  key={g}
                  className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
                    hasCover
                      ? "bg-white/15 text-white"
                      : "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                  }`}
                >
                  {g}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col gap-4">
          {artist.bio && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Bio</p>
              <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">{artist.bio}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {artist.instruments?.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Instruments</p>
                <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">{artist.instruments.join(", ")}</p>
              </div>
            )}
            {artist.experience_level && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Experience</p>
                <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">{artist.experience_level}</p>
              </div>
            )}
          </div>

          {(artist.website || artist.social_links?.length > 0) && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Links</p>
              <div className="mt-1.5 flex flex-wrap gap-3">
                {artist.website && (
                  <a
                    className="text-sm text-amber-600 underline underline-offset-2 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300"
                    href={artist.website}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Website
                  </a>
                )}
                {(artist.social_links || []).map((link) => (
                  <a
                    key={link}
                    className="text-sm text-amber-600 underline underline-offset-2 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300"
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {link.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-zinc-100 pt-4 dark:border-white/[0.06]">
            <Button
              type="button"
              className="btn-primary w-full"
              onClick={() => { onClose(); onContact(artist); }}
            >
              Contact {artist.name}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
